import { PhotoCamera } from '@project-lary/react-material-symbols';
import { IS_NATIVE } from 'gcs-common/constants/deviceConstants';
import Camera from 'gcs-common/design-system/camera/camera';
import Anchor from 'gcs-common/design-system/ui/anchor';
import { Button, ButtonSize, ButtonVariant } from 'gcs-common/design-system/ui/button';
import { Dialog, DialogContent, DialogTrigger } from 'gcs-common/design-system/ui/dialog';
import { getPictureFromCamera } from 'gcs-common/helper/capacitorImageHelper';
import { useRef, useState } from 'react';
import IconButton from './icon-button';

interface ProfilePictureProps {
  value?: string;
  onChange: (value: string) => void;
}

const ProfilePicture: React.FC<ProfilePictureProps> = ({ value, onChange }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [step, setStep] = useState<'select' | 'camera'>('select');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleCapturedImages = (images: string[]) => {
    if (images.length > 0) {
      const latestImage = images[images.length - 1];
      if (latestImage) {
        onChange(latestImage);
        setShowDialog(false);
      }
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>, onChange: (value: string) => void) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result && typeof reader.result === 'string') {
          onChange(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCaptureWithCapacitor = async () => {
    try {
      const result = await getPictureFromCamera('CAMERA', 'profile_image', false, false);
      if (result && typeof result === 'string') {
        onChange(result);
      }
    } catch (error) {
      console.error('Error capturing image with Capacitor:', error);
    }
  };
  const handleCaptureWithCapacitorKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCaptureWithCapacitor();
    }
  };

  return (
    <div className="flex items-center justify-center">
      {IS_NATIVE ? (
        <div
          role="button"
          tabIndex={0}
          className="flex size-[100px] cursor-pointer items-center justify-center rounded-full bg-neutral-5"
          onClick={handleCaptureWithCapacitor}
          onKeyDown={handleCaptureWithCapacitorKeyDown}
        >
          {value ? (
            <div className="relative size-full cursor-pointer rounded-full object-cover">
              <img src={value} alt="Profile" className="size-full cursor-pointer rounded-full object-cover" />
              <IconButton
                Icon={PhotoCamera}
                className="absolute top-16 right-0 cursor-pointer rounded-xl bg-neutral-10 text-center text-white"
                size="l"
              />
            </div>
          ) : (
            <IconButton Icon={PhotoCamera} className="flex cursor-pointer rounded-md text-accent-9" size="l" />
          )}
        </div>
      ) : (
        <Dialog open={showDialog} onOpenChange={(open) => setShowDialog(open)}>
          <DialogTrigger asChild>
            <div className="flex size-[100px] items-center justify-center rounded-full bg-neutral-5">
              {value ? (
                <div className="relative size-full cursor-pointer rounded-full object-cover">
                  <img src={value} alt="Profile" className="size-full cursor-pointer rounded-full object-cover" />
                  <IconButton
                    Icon={PhotoCamera}
                    className="absolute top-16 right-0 cursor-pointer rounded-xl bg-neutral-10 text-white"
                    size="l"
                  />
                </div>
              ) : (
                <IconButton Icon={PhotoCamera} className="flex cursor-pointer rounded-md text-accent-9" size="l" />
              )}
            </div>
          </DialogTrigger>
          <DialogContent className="m-0 w-96 border-none py-5 outline-none" hideDefaultCloseButton>
            {step === 'select' ? (
              <div className="flex flex-col items-center justify-center gap-4">
                <Button
                  variant={ButtonVariant.soft_neutral}
                  id="select-gallery-button"
                  size={ButtonSize.l}
                  className="w-full"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Ein Bild hochladen
                </Button>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  className="hidden"
                  onChange={(event) => {
                    handleFileUpload(event, onChange);
                    setShowDialog(false);
                  }}
                />
                <Button
                  variant={ButtonVariant.soft_neutral}
                  id="select-camera-button"
                  size={ButtonSize.l}
                  className="w-full"
                  onClick={() => setStep('camera')}
                >
                  Foto machen
                </Button>
                <Anchor onClick={() => setShowDialog(false)} className="no-underline">
                  Abbrechen
                </Anchor>
              </div>
            ) : (
              <Camera
                onClosed={() => {
                  setShowDialog(false);
                  setStep('select');
                }}
                onCapturedImages={handleCapturedImages}
              />
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ProfilePicture;
